/**
 * Contract Error Boundary - Error boundary component for contract editor features
 * Catches React errors and displays the ContractErrorFallback UI
 */

import React, { Component, ReactNode } from 'react';
import { ContractErrorFallback } from './ContractErrorFallback';

// Sentry type definitions for window object
interface SentryHub {
  captureException(error: Error, captureContext?: {
    contexts?: {
      react?: {
        // React's ErrorInfo types componentStack as `string | null | undefined`
        // (it is null when React cannot build a stack), so the local Sentry shim
        // has to accept null rather than the call site defaulting it.
        componentStack?: string | null;
      };
    };
    tags?: Record<string, string>;
  }): string;
}

declare global {
  interface Window {
    Sentry?: SentryHub;
  }
}

interface Props {
  children: ReactNode;
  /** Fallback component to render on error */
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ContractErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('Contract error boundary caught error:', error, errorInfo);

    // Log to Sentry if available
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          component: 'ContractErrorBoundary',
          feature: 'contract-editor',
        },
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || ContractErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ContractErrorBoundary;